import React, { useState } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
} from "@mui/material";
import TopNavBar from "../components/TopNavBar";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { postDeviceTelemetry } from "../api/telemetry";

export default function TestTelemetryPage() {
  const defaultForm = {
    rcDeviceId: "TEST-ROBO-12345678910111213",
    tbDeviceId: "42754d80-ca9f-11f0-96fc-875ff654df18", //This is the real test robot in thingsboard
    serialNumber: "d2955fe8-5791-1538-11b2-00000000000",
    ts: Date.now(),
    batteryLevel: 100,
    stationFilterStatus: "normal",
    motorFilterStatus: "normal",
    dustBagStatus: "normal",
    operatingState: "charged",
    homingReason: "none",
    cleaningStep: "null",
    movementState: "charging",
    cleaningMode: "stop",
    cleaningType: "vacuumAndMopTogether",
    cleaningArea: "none",
    cleaningExtent: -1,
    nearObject: "TABLE",
    remainingTime: -1,
    blockingStatus: "unblocked",
    microphoneState: "muted",
    deviceName: "RC-Unit-1",
    customerName: "Johnny Cleaners",
  };

  const [form, setForm] = useState(defaultForm);
  const [useCurrentTime, setUseCurrentTime] = useState(true);
  const [logs, setLogs] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openRows, setOpenRows] = useState({});

  // Helper to convert Unix timestamp to time format (HH:mm:ss)
  const timestampToTime = (ts) => {
    const date = new Date(Number(ts));
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  // Helper to convert time format to Unix timestamp (today's date + time)
  const timeToTimestamp = (timeStr) => {
    const [hours, minutes, seconds] = timeStr.split(":");
    const now = new Date();
    now.setHours(parseInt(hours, 10));
    now.setMinutes(parseInt(minutes, 10));
    now.setSeconds(parseInt(seconds, 10));
    now.setMilliseconds(0);
    return now.getTime();
  };

  const handleChange = (key) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleTimeChange = (e) => {
    const timestamp = timeToTimestamp(e.target.value);
    setForm((s) => ({ ...s, ts: timestamp }));
  };

  const sendTelemetryData = async () => {
    // Build payload with proper types
    const payload = {
      deviceId: String(form.rcDeviceId),
      tbDeviceId: String(form.tbDeviceId),
      serialNumber: String(form.serialNumber),
      ts: useCurrentTime ? Date.now() : Number(form.ts),
      batteryLevel: Number(form.batteryLevel) || 0,
      stationFilterStatus: String(form.stationFilterStatus),
      motorFilterStatus: String(form.motorFilterStatus),
      dustBagStatus: String(form.dustBagStatus),
      operatingState: String(form.operatingState),
      homingReason: String(form.homingReason),
      cleaningStep: Number(form.cleaningStep) || 0,
      movementState: String(form.movementState),
      cleaningMode: String(form.cleaningMode),
      cleaningType: String(form.cleaningType),
      cleaningArea: String(form.cleaningArea),
      cleaningExtent: Number(form.cleaningExtent) || 0,
      nearObject: String(form.nearObject),
      remainingTime: Number(form.remainingTime) || 0,
      blockingStatus: String(form.blockingStatus),
      microphoneState: String(form.microphoneState),
      deviceName: String(form.deviceName),
      customerName: String(form.customerName),
    };

    const pushedData = await postDeviceTelemetry(payload);
    if (pushedData.error) {
      setSnackbar({
        open: true,
        message: pushedData.error,
        severity: "error",
      });
      return;
    }

    // keep form values as-is, add log entry (in-memory)
    setLogs((prev) => [{ payload, addedAt: Date.now() }, ...prev]);

    setSnackbar({
      open: true,
      message: "Telemetry queued (in-memory)",
      severity: "success",
    });
  };

  const clearLogs = () => setLogs([]);

  const toggleRow = (key) => {
    setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const statusOptions = ["normal", "warning", "fault"];
  const operatingOptions = [
    "calibrating",
    "charged",
    "charging",
    "chargingForRemainingJob",
    "cleaning",
    "descaling",
    "dryingMop",
    "emitDust",
    "error",
    "exploring",
    "findingPet",
    "flexCharged",
    "homing",
    "idle",
    "manual",
    "messaging",
    "monitoring",
    "monitoringAutomation",
    "patrol",
    "powerSaving",
    "preparingWater",
    "processing",
    "relocal",
    "spinDrying",
    "sterilizingMop",
    "supplyingWater",
    "waitingForDescaling",
    "washingMop",
    "welcoming",
  ];
  const movementOptions = [
    "after",
    "alarm",
    "charging",
    "cleaning",
    "homing",
    "idle",
    "pause",
    "point",
    "powerOff",
    "reserve",
  ];
  const homingOptions = ["none", "washingMop", "cleaningCompleted", "pet"];
  const cleaningTypeOptions = ["vacuumOnly", "mopOnly", "vacuumAndMopTogether"];
  const cleaningModes = ["stop", "area"];
  const blockingOptions = ["unblocked", "blocked"];
  const microphoneOptions = ["muted", "unmuted"];

  return (
    <>
      <TopNavBar heading={"Test Telemetry to Thingsboard"} />
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send Telemetry
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCurrentTime}
                  onChange={(e) => setUseCurrentTime(e.target.checked)}
                />
              }
              label="Use current timestamp (now)"
            />
            {!useCurrentTime && (
              <TextField
                label="Time (HH:mm:ss)"
                type="time"
                value={timestampToTime(form.ts)}
                onChange={handleTimeChange}
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                slotProps={{
                  InputLabelProps: {
                    shrink: true,
                  },
                }}
                helperText={`Full timestamp: ${new Date(
                  form.ts
                ).toLocaleString()}`}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 2,
            }}
          >
            <TextField
              label="RC Device ID"
              value={form.rcDeviceId}
              onChange={handleChange("rcDeviceId")}
              size="small"
            />
            <TextField
              label="Serial Number"
              value={form.serialNumber}
              onChange={handleChange("serialNumber")}
              size="small"
            />
            <TextField
              label="Thingsboard Device ID"
              value={form.tbDeviceId}
              onChange={handleChange("tbDeviceId")}
              size="small"
            />
            <TextField
              label="Battery %"
              type="number"
              value={form.batteryLevel}
              onChange={handleChange("batteryLevel")}
              size="small"
            />
            <TextField
              label="Station Filter"
              select
              value={form.stationFilterStatus}
              onChange={handleChange("stationFilterStatus")}
              size="small"
            >
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Motor Filter"
              select
              value={form.motorFilterStatus}
              onChange={handleChange("motorFilterStatus")}
              size="small"
            >
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Dust Bag"
              select
              value={form.dustBagStatus}
              onChange={handleChange("dustBagStatus")}
              size="small"
            >
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Operating State"
              select
              value={form.operatingState}
              onChange={handleChange("operatingState")}
              size="small"
            >
              {operatingOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Homing Reason"
              select
              value={form.homingReason}
              onChange={handleChange("homingReason")}
              size="small"
            >
              {homingOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cleaning Step"
              value={form.cleaningStep}
              onChange={handleChange("cleaningStep")}
              size="small"
            />

            <TextField
              label="Movement State"
              select
              value={form.movementState}
              onChange={handleChange("movementState")}
              size="small"
            >
              {movementOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cleaning Mode"
              select
              value={form.cleaningMode}
              onChange={handleChange("cleaningMode")}
              size="small"
            >
              {cleaningModes.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cleaning Type"
              select
              value={form.cleaningType}
              onChange={handleChange("cleaningType")}
              size="small"
            >
              {cleaningTypeOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Cleaning Area"
              value={form.cleaningArea}
              onChange={handleChange("cleaningArea")}
              size="small"
            />

            <TextField
              label="Cleaning Extent"
              type="number"
              value={form.cleaningExtent}
              onChange={handleChange("cleaningExtent")}
              size="small"
            />

            <TextField
              label="Near Object"
              value={form.nearObject}
              onChange={handleChange("nearObject")}
              size="small"
            />

            <TextField
              label="Remaining Time (s)"
              type="number"
              value={form.remainingTime}
              onChange={handleChange("remainingTime")}
              size="small"
            />

            <TextField
              label="Blocking Status"
              select
              value={form.blockingStatus}
              onChange={handleChange("blockingStatus")}
              size="small"
            >
              {blockingOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Microphone State"
              select
              value={form.microphoneState}
              onChange={handleChange("microphoneState")}
              size="small"
            >
              {microphoneOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Device Name"
              value={form.deviceName}
              onChange={handleChange("deviceName")}
              size="small"
            />

            <TextField
              label="Customer Name"
              value={form.customerName}
              onChange={handleChange("customerName")}
              size="small"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={sendTelemetryData}>
              Send Telemetry
            </Button>
            <Button variant="outlined" onClick={() => setForm(defaultForm)}>
              Reset Form
            </Button>
            <Button variant="outlined" color="error" onClick={clearLogs}>
              Clear Logs
            </Button>
          </Box>
        </Paper>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Telemetry Log (in-memory)
        </Typography>
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Battery</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Movement</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Payload</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No telemetry sent yet.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((l, i) => {
                  const key = `${l.addedAt}-${i}`;
                  return (
                    <React.Fragment key={key}>
                      <TableRow>
                        <TableCell>
                          {new Date(l.addedAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{l.payload.deviceId}</TableCell>
                        <TableCell>{l.payload.batteryLevel}%</TableCell>
                        <TableCell>{l.payload.operatingState}</TableCell>
                        <TableCell>{l.payload.movementState}</TableCell>
                        <TableCell>{l.payload.cleaningType}</TableCell>

                        {/* show small preview and toggle button */}
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {JSON.stringify(l.payload).slice(0, 120) +
                                (JSON.stringify(l.payload).length > 120
                                  ? "..."
                                  : "")}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(key)}
                              aria-label={
                                openRows[key]
                                  ? "collapse payload"
                                  : "expand payload"
                              }
                              aria-expanded={!!openRows[key]}
                            >
                              {openRows[key] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* expanded row with full payload */}
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={7}
                        >
                          <Collapse
                            in={!!openRows[key]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ margin: 1 }}>
                              <pre
                                style={{
                                  margin: 0,
                                  maxWidth: "100%",
                                  overflow: "auto",
                                }}
                              >
                                {JSON.stringify(l.payload, null, 2)}
                              </pre>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
